import { charWidth, issueDot, wrapText } from './cardSvg';
import type { Kid } from './contentLayout';
import { tile } from './contentSvg';
import { statusThemeColor } from './status';
import type { Theme } from './theme';

/**
 * Zeitpunkte und Zeiträume am Zeitstrahl: runde Marke auf der Linie (Zeitpunkt: Pin, Zeitraum: Sanduhr in der Mitte eines
 * leuchtenden Balkens) und darüber eine Vorschaukarte mit Zeit, Titel, optional Kurzbeschreibung und Inhalten. Alles als SVG,
 * im Modellraum (skaliert mit dem Zoom wie die übrigen Karten).
 */

/** Durchmesser der runden Marke; zugleich die Höhe von Zeitpunkt und Zeitraum auf dem Zeitstrahl */
export const BADGE = 36;
/** Höhe des Balkens eines Zeitraums */
export const BAR = 14;
/** Breite der Vorschaukarte */
export const PREVIEW_W = 256;

const PAD = 14;
const TILE = 40;
const TILE_GAP = 8;
const MAX_TILES = 4;
const FONT = 'Inter, system-ui, sans-serif';
const esc = (s: string): string => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Was die Vorschau zeigt (Titel immer). */
export interface PreviewShow {
  time: boolean;
  content: boolean;
  description: boolean;
}

/** Einstellungen der Vorschau eines Nodes; fehlende Angaben: Zeit und Inhalte an, Kurzbeschreibung aus (ein altes `collapsed` gilt nicht mehr). */
export function previewShowOf(raw: { time?: boolean; content?: boolean; description?: boolean } | null | undefined): PreviewShow {
  return { time: raw?.time ?? true, content: raw?.content ?? true, description: raw?.description ?? false };
}

export interface PreviewSpec {
  title: string;
  /** Zeit (Zeitpunkt bzw. „Beginn – Ende“), leer wenn unbekannt */
  when: string;
  /** Kurzbeschreibung als reiner Text */
  description: string;
  kids: Kid[];
  /** Rand und Marke */
  color: string;
  /** Füllung (eine Szene im Kapitel: dessen blasse Farbe); fehlt: ein heller Ton von `color` */
  fill?: string;
  /** Status der Szene als Fähnchen oben rechts (Knopf in der Erzähllinie); fehlt bzw. leer: kein Fähnchen */
  status?: string;
  show: PreviewShow;
}

export interface PreviewLayout {
  w: number;
  h: number;
  timeY: number | null;
  /** Fähnchen mit dem Status: Breite und Mitte der Zeile, null ohne */
  flag: { w: number; y: number } | null;
  title: string[];
  titleY: number;
  description: string[];
  descriptionY: number;
  tiles: Kid[];
  more: number;
  tilesY: number | null;
}

/** Aufteilung der Karte; die Höhe richtet sich nach dem gezeigten Inhalt. */
export function previewLayout(spec: PreviewSpec): PreviewLayout {
  const inner = PREVIEW_W - 2 * PAD;
  let y = PAD;
  const flagW = spec.status ? Math.min(inner, Math.ceil(spec.status.length * charWidth(FLAG_FONT, true)) + 2 * FLAG_PAD) : 0;
  const time = spec.show.time && !!spec.when;
  // das Fähnchen steht rechts neben der Zeit bzw. dem Titel; passt es nicht neben die Zeit, bekommt es eine eigene Zeile darüber
  let flag: PreviewLayout['flag'] = null;
  if (flagW && time && spec.when.length * charWidth(14, true) + FLAG_GAP + flagW > inner) {
    flag = { w: flagW, y: y + 8 };
    y += 24;
  } else if (flagW) flag = { w: flagW, y: time ? y + 7 : y + 10 };
  const timeY = time ? y + 12 : null;
  if (timeY !== null) y += 22;
  const title = wrapText(spec.title || '–', inner - (timeY === null && flagW ? flagW + FLAG_GAP : 0), 18, 3, true);
  const titleY = y + 17;
  y += title.length * 22 + 2;
  const description = spec.show.description && spec.description ? wrapText(spec.description, inner, 14, 3) : [];
  const descriptionY = y + 15;
  if (description.length) y += 6 + description.length * 19;
  const shown = spec.show.content ? spec.kids : [];
  const room = shown.length > MAX_TILES ? MAX_TILES - 1 : MAX_TILES;
  const tiles = shown.slice(0, room);
  const more = shown.length - tiles.length;
  const tilesY = tiles.length ? y + 10 : null;
  if (tilesY !== null) y += 10 + TILE;
  return { w: PREVIEW_W, h: Math.max(y + PAD, 58), timeY, flag, title, titleY, description, descriptionY, tiles, more, tilesY };
}

/** Kachel der Inhalte unter einem Punkt der Karte (Kartenkoordinaten in Pixeln), sonst null. */
export function tileAt(layout: PreviewLayout, x: number, y: number): Kid | null {
  if (layout.tilesY === null || y < layout.tilesY || y > layout.tilesY + TILE) return null;
  const i = layout.tiles.findIndex((_, k) => x >= PAD + k * (TILE + TILE_GAP) && x <= PAD + k * (TILE + TILE_GAP) + TILE);
  return i >= 0 ? layout.tiles[i]! : null;
}

/** Mischt `color` mit `into` (beide `#rrggbb`); `amount` 0 = nur `color`, 1 = nur `into`. */
export function tint(color: string, into: string, amount: number): string {
  const rgb = (c: string): number[] | null => {
    const full = /^#[0-9a-f]{3}$/i.test(c) ? `#${[...c.slice(1)].map((h) => h + h).join('')}` : c;
    return /^#[0-9a-f]{6}$/i.test(full) ? [1, 3, 5].map((i) => parseInt(full.slice(i, i + 2), 16)) : null;
  };
  const a = rgb(color);
  const b = rgb(into);
  if (!a || !b) return color;
  return `#${a.map((x, i) => Math.round(x + (b[i]! - x) * amount).toString(16).padStart(2, '0')).join('')}`;
}

const FLAG_FONT = 11;
const FLAG_PAD = 8;
const FLAG_GAP = 8;
const FLAG_H = 20;

/** Fähnchen mit dem Status: Pille mit Rand und Schrift in der Farbe des Status auf einem hellen Ton davon, rechtsbündig. */
const statusFlag = (right: number, cy: number, w: number, status: string, theme: Theme): string => {
  const c = statusThemeColor(status, theme);
  const fill = tint(c, theme.surface, theme.dark ? 0.75 : 0.85);
  const x = right - w;
  return `<g data-status="${esc(status)}"><rect x="${x}" y="${cy - FLAG_H / 2}" width="${w}" height="${FLAG_H}" rx="${FLAG_H / 2}" fill="${fill}" stroke="${esc(c)}" stroke-width="1.5"/><text x="${x + w / 2}" y="${cy + 4}" text-anchor="middle" font-family="${FONT}" font-size="${FLAG_FONT}" font-weight="700" fill="${theme.dark ? theme.text : esc(tint(c, '#000000', 0.25))}">${esc(status)}</text></g>`;
};

/** Zeit auf der Vorschaukarte: einheitlich ein sehr dunkles bzw. (dunkler Modus) sehr helles Grau, unabhängig von der Farbe des Elements */
export const timeColor = (theme: Theme): string => (theme.dark ? '#e4e4e4' : '#333333');

/** Vorschaukarte als SVG: Zeit in einem neutralen Grau, fetter Titel, Kurzbeschreibung, Kacheln der Inhalte, auf Wunsch der Status oben rechts. */
export function previewSvg(spec: PreviewSpec, layout: PreviewLayout, theme: Theme, imageOf: (assetId: string) => string | null): string {
  const { w, h } = layout;
  const c = esc(spec.color);
  const fill = spec.fill ? (theme.dark ? tint(spec.fill, theme.surface, 0.78) : spec.fill) : tint(spec.color, theme.surface, theme.dark ? 0.86 : 0.94);
  const p: string[] = [`<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">`];
  p.push(`<rect x="1.5" y="1.5" width="${w - 3}" height="${h - 3}" rx="14" fill="${fill}" stroke="${c}" stroke-width="2.5"/>`);
  if (layout.timeY !== null) p.push(`<text x="${PAD}" y="${layout.timeY}" font-family="${FONT}" font-size="14" font-weight="600" fill="${timeColor(theme)}">${esc(spec.when)}</text>`);
  layout.title.forEach((line, i) => p.push(`<text x="${PAD}" y="${layout.titleY + i * 22}" font-family="${FONT}" font-size="18" font-weight="800" fill="${theme.text}">${esc(line)}</text>`));
  layout.description.forEach((line, i) => p.push(`<text x="${PAD}" y="${layout.descriptionY + i * 19}" font-family="${FONT}" font-size="14" fill="${theme.muted}">${esc(line)}</text>`));
  if (layout.tilesY !== null) {
    layout.tiles.forEach((kid, i) => {
      p.push(`<g transform="translate(${PAD + i * (TILE + TILE_GAP)} ${layout.tilesY})">${tile(kid, TILE, 1, theme, kid.imageId ? imageOf(kid.imageId) : null, i)}</g>`);
    });
    if (layout.more > 0) {
      const x = PAD + layout.tiles.length * (TILE + TILE_GAP);
      p.push(`<rect x="${x + 1}" y="${layout.tilesY + 1}" width="${TILE - 2}" height="${TILE - 2}" rx="7" fill="none" stroke="${theme.muted}" stroke-dasharray="3 3"/>`);
      p.push(`<text x="${x + TILE / 2}" y="${layout.tilesY + TILE / 2 + 5}" text-anchor="middle" font-family="${FONT}" font-size="13" font-weight="700" fill="${theme.muted}">+${layout.more}</text>`);
    }
  }
  if (layout.flag && spec.status) p.push(statusFlag(w - PAD + 4, layout.flag.y, layout.flag.w, spec.status, theme));
  p.push('</svg>');
  return p.join('');
}

/** Reduzierte Vorschau: nur der Titel (höchstens zwei Zeilen), Maße so knapp wie der Text */
const TITLE_ONLY = { size: 16, line: 20, pad: 5 } as const;

export function previewTitleLayout(title: string): { w: number; h: number; lines: string[] } {
  const { size, line, pad } = TITLE_ONLY;
  const lines = wrapText(title || '–', PREVIEW_W - 2 * pad, size, 2, true);
  const longest = Math.max(...lines.map((l) => l.length));
  return { w: Math.min(PREVIEW_W, Math.ceil(longest * charWidth(size, true)) + 2 * pad), h: lines.length * line + 2 * pad, lines };
}

/**
 * Reduzierte Vorschau als SVG: fetter Titel ohne Box, mittig, mit einem Rand in der Farbe der Fläche (lesbar über Linien). Ein
 * Hintergrundereignis (PF 15) schlicht: kursiv, gedämpft.
 */
export function previewTitleSvg(title: string, theme: Theme, background = false): string {
  const { w, h, lines } = previewTitleLayout(title);
  const { size, line, pad } = TITLE_ONLY;
  const look = background ? `font-weight="600" font-style="italic" fill="${theme.muted}"` : `font-weight="800" fill="${theme.text}"`;
  const text = lines
    .map((l, i) => `<text x="${w / 2}" y="${pad + i * line + size - 1}" text-anchor="middle" font-family="${FONT}" font-size="${size}" ${look} stroke="${theme.canvas}" stroke-width="4" stroke-linejoin="round" paint-order="stroke">${esc(l)}</text>`)
    .join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">${text}</svg>`;
}

const PIN = (c: string): string =>
  `<path d="M12 21.5s-6.5-5.8-6.5-11.2a6.5 6.5 0 0 1 13 0C18.5 15.7 12 21.5 12 21.5z" fill="none" stroke="${c}" stroke-width="2" stroke-linejoin="round"/><circle cx="12" cy="10.3" r="2.4" fill="none" stroke="${c}" stroke-width="2"/>`;
const HOURGLASS = (c: string): string =>
  `<path d="M6.5 3h11M6.5 21h11M7.5 3c0 5 9 6.5 9 9s-9 4-9 9M16.5 3c0 5-9 6.5-9 9s9 4 9 9" fill="none" stroke="${c}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`;

const badge = (cx: number, cy: number, color: string, theme: Theme, icon: (c: string) => string): string => {
  const r = BADGE / 2 - 1.5;
  const fill = tint(color, theme.dark ? '#000000' : theme.surface, theme.dark ? 0.55 : 0.82);
  return `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${fill}" stroke="${esc(color)}" stroke-width="2"/><g transform="translate(${cx - 10} ${cy - 10}) scale(${20 / 24})">${icon(esc(color))}</g>`;
};

/** Zeitpunkt: runde Marke mit Pin. */
export function eventBadgeSvg(color: string, theme: Theme, issues = 0): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${BADGE}" height="${BADGE}" viewBox="0 0 ${BADGE} ${BADGE}">${badge(BADGE / 2, BADGE / 2, color, theme, PIN)}${issueDot(BADGE - 7, 7, issues, theme)}</svg>`;
}


/** Hintergrundereignis (PF 15): schlichte Marke – ein gestrichelter Ring auf der Farbe der Fläche mit einem Punkt in der Mitte. */
const backgroundRing = (cx: number, cy: number, color: string, theme: Theme): string =>
  `<circle cx="${cx}" cy="${cy}" r="${BADGE / 2 - 8}" fill="${theme.canvas}" stroke="${esc(color)}" stroke-width="2" stroke-dasharray="3.5 2.5"/><circle cx="${cx}" cy="${cy}" r="2.5" fill="${esc(color)}"/>`;

/** Zeitpunkt eines Hintergrundereignisses: der Ring statt der Marke mit Pin */
export function backgroundBadgeSvg(color: string, theme: Theme, issues = 0): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${BADGE}" height="${BADGE}" viewBox="0 0 ${BADGE} ${BADGE}">${backgroundRing(BADGE / 2, BADGE / 2, color, theme)}${issueDot(BADGE - 9, 9, issues, theme)}</svg>`;
}

/** Zeitraum eines Hintergrundereignisses: ein schmaler, blasser Balken mit gestricheltem Rand, in der Mitte der Ring */
export function backgroundPeriodSvg(width: number, color: string, theme: Theme, issues = 0): string {
  const w = Math.max(width, BADGE);
  const bar = BAR / 2;
  const y = (BADGE - bar) / 2;
  const c = esc(color);
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${BADGE}" viewBox="0 0 ${w} ${BADGE}"><rect x="1" y="${y}" width="${w - 2}" height="${bar}" rx="${bar / 2}" fill="${c}" fill-opacity="0.22" stroke="${c}" stroke-width="1.5" stroke-dasharray="4 3"/>${backgroundRing(w / 2, BADGE / 2, color, theme)}${issueDot(w / 2 + 9, 9, issues, theme)}</svg>`;
}

/**
 * Unscharfer Bereich einer ungenauen Zeit (AP-19): ein Band in der Farbe der Szene, das zu beiden Seiten weich ausläuft – blass genug,
 * dass Marken und Balken davor klar bleiben (ein Hintergrundereignis noch blasser).
 */
export function blurSvg(width: number, height: number, color: string, theme: Theme, faint = false): string {
  const w = Math.max(width, 2);
  const fade = Math.min(0.35, 28 / w);
  const a = (theme.dark ? 0.36 : 0.24) * (faint ? 0.6 : 1);
  const c = esc(color);
  const stops = [
    [0, 0],
    [fade, a],
    [1 - fade, a],
    [1, 0],
  ]
    .map(([o, op]) => `<stop offset="${o}" stop-color="${c}" stop-opacity="${op}"/>`)
    .join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${height}" viewBox="0 0 ${w} ${height}"><defs><linearGradient id="g" x1="0" x2="1" y1="0" y2="0">${stops}</linearGradient></defs><rect x="0" y="0" width="${w}" height="${height}" rx="${height / 2}" fill="url(#g)"/></svg>`;
}

/** Zeitraum: leuchtender Balken über die ganze Länge, in der Mitte die runde Marke mit Sanduhr. */
export function periodSvg(width: number, color: string, theme: Theme, issues = 0): string {
  const w = Math.max(width, BADGE);
  const y = (BADGE - BAR) / 2;
  const c = esc(color);
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${BADGE}" viewBox="0 0 ${w} ${BADGE}"><rect x="1" y="${y}" width="${w - 2}" height="${BAR}" rx="${BAR / 2}" fill="${c}" fill-opacity="0.88"/><rect x="1" y="${y}" width="${w - 2}" height="${BAR}" rx="${BAR / 2}" fill="none" stroke="${tint(color, '#ffffff', 0.35)}" stroke-width="1.5"/>${badge(w / 2, BADGE / 2, color, theme, HOURGLASS)}${issueDot(w / 2 + 11, 7, issues, theme)}</svg>`;
}
